// Chat history bundles: used to hand history to a newly linked device (QR
// flow) and for encrypted backups. Bundles are gzipped JSON, sealed with
// AES-256-GCM using a key the server never sees:
//   * linking: ECDH P-256 between the two devices (new device's public key
//     travels only inside the QR code), then HKDF;
//   * backups: PBKDF2-SHA256 from the user's backup password.
import type { ContactProfile, Chat, DB, Msg } from "./db";

export interface Bundle {
  v: 1;
  account: string;
  exportedAt: number;
  chats: Chat[];
  messages: Msg[];
  profiles: ContactProfile[];
  cursor?: string; // MAM position, so the new device doesn't re-sync all of it
  complete?: boolean; // the whole history, not just the last 30 days
  cleared?: Record<string, number>; // chat -> deleted up to (automatic backup)
}

/** The phone app gets the whole chat history; the web gets the last 30 days. */
export const isPhone = (platform: string) => platform === "android" || platform === "ios";

export async function exportHistory(db: DB, account: string, opts: { days?: number; limit?: number } = {}): Promise<Bundle> {
  const limit = opts.limit ?? 20_000;
  const since = opts.days ? Date.now() - opts.days * 86_400_000 : 0;
  // Deleted-for-me tombstones (no content) go too, so those messages stay
  // hidden on the other device instead of showing up as unreadable.
  const all = (await db.getAll("messages")).filter((m) => !m.undecryptable && m.ts >= since && (!m.expiresAt || m.expiresAt > Date.now()));
  all.sort((a, b) => b.ts - a.ts);
  // View-once photos/videos never travel with the history: they go as
  // already opened, so another device can't open them again.
  const messages = all
    .slice(0, limit)
    .map((m) => (m.viewOnce ? { ...m, viewed: true, body: "", media: m.media ? { mime: m.media.mime, size: 0 } : undefined } : { ...m, media: m.media ? { ...m.media, blob: undefined } : undefined }));
  return {
    v: 1,
    account,
    exportedAt: Date.now(),
    chats: await db.getAll("chats"),
    messages,
    profiles: await db.getAll("profiles"),
    cursor: (await db.get("kv", "mam:cursor")) as string | undefined,
  };
}

/** Union of two bundles of the same account (the automatic backup): `mine`
 * wins for messages both have; deleted chats and expired messages go. */
export function mergeBundles(mine: Bundle, other: Bundle, limit = 200_000): Bundle {
  const now = Date.now();
  const cleared = { ...(other.cleared ?? {}) };
  for (const [j, t] of Object.entries(mine.cleared ?? {})) cleared[j] = Math.max(cleared[j] ?? 0, t);
  const keep = (m: Msg) => !(m.expiresAt && m.expiresAt <= now) && !(cleared[m.chat] && m.ts <= cleared[m.chat]);
  const msgs = new Map<string, Msg>();
  for (const m of [...other.messages, ...mine.messages]) if (keep(m)) msgs.set(m.key, m);
  const chats = new Map<string, Chat>();
  for (const c of [...other.chats, ...mine.chats]) {
    const cur = chats.get(c.jid);
    if (!cur || cur.lastTs <= c.lastTs) chats.set(c.jid, c);
  }
  for (const [j, t] of Object.entries(cleared)) if ((chats.get(j)?.lastTs ?? Infinity) <= t) chats.delete(j);
  const profiles = new Map([...other.profiles, ...mine.profiles].map((p) => [p.jid, p]));
  return {
    v: 1,
    account: mine.account,
    exportedAt: now,
    chats: [...chats.values()],
    messages: [...msgs.values()].sort((a, b) => b.ts - a.ts).slice(0, limit),
    profiles: [...profiles.values()],
    cursor: mine.cursor ?? other.cursor,
    complete: mine.complete || other.complete,
    cleared,
  };
}

/** Only the last `days` days of a bundle (what the web keeps). */
export function lastDays(b: Bundle, days: number): Bundle {
  const since = Date.now() - days * 86_400_000;
  return { ...b, messages: b.messages.filter((m) => m.ts >= since), complete: false };
}

/** Merge a bundle into this device's store. Returns messages added. */
export async function importHistory(db: DB, b: Bundle, account: string): Promise<number> {
  if (b.v !== 1 || b.account !== account) throw new Error("this history belongs to another account");
  let added = 0;
  const tx = db.transaction(["messages", "chats", "profiles", "kv"], "readwrite");
  const msgs = tx.objectStore("messages");
  for (const m of b.messages) {
    const cur = await msgs.get(m.key);
    if (!cur || cur.undecryptable) {
      await msgs.put(m);
      added++;
    }
  }
  const chats = tx.objectStore("chats");
  for (const c of b.chats) {
    const cur = await chats.get(c.jid);
    if (!cur || cur.lastTs <= c.lastTs) await chats.put({ ...c, unread: cur?.unread ?? 0 });
  }
  for (const p of b.profiles) await tx.objectStore("profiles").put(p);
  if (b.cursor && !(await tx.objectStore("kv").get("mam:cursor"))) await tx.objectStore("kv").put(b.cursor, "mam:cursor");
  await tx.done;
  return added;
}

// ---- packing -----------------------------------------------------------------------

async function gzip(data: Uint8Array<ArrayBuffer>): Promise<Uint8Array<ArrayBuffer>> {
  const s = new Blob([data]).stream().pipeThrough(new CompressionStream("gzip"));
  return new Uint8Array(await new Response(s).arrayBuffer());
}

async function gunzip(data: Uint8Array<ArrayBuffer>): Promise<Uint8Array<ArrayBuffer>> {
  const s = new Blob([data]).stream().pipeThrough(new DecompressionStream("gzip"));
  return new Uint8Array(await new Response(s).arrayBuffer());
}

/** Seal a bundle: gzip then AES-GCM. Output: iv(12) || ciphertext. */
export async function seal(key: CryptoKey, b: Bundle): Promise<Blob> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const packed = await gzip(new TextEncoder().encode(JSON.stringify(b)));
  const ct = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, packed);
  return new Blob([iv, ct], { type: "application/octet-stream" });
}

export async function unseal(key: CryptoKey, data: ArrayBuffer): Promise<Bundle> {
  const bytes = new Uint8Array(data);
  let plain: ArrayBuffer;
  try {
    plain = await crypto.subtle.decrypt({ name: "AES-GCM", iv: bytes.subarray(0, 12) }, key, bytes.subarray(12));
  } catch {
    throw new Error("couldn't decrypt: wrong password or damaged data");
  }
  return JSON.parse(new TextDecoder().decode(await gunzip(new Uint8Array(plain))));
}

// ---- keys --------------------------------------------------------------------------

const b64u = {
  from: (b: ArrayBuffer | Uint8Array) => btoa(String.fromCharCode(...new Uint8Array(b instanceof Uint8Array ? b : new Uint8Array(b)))).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, ""),
  to: (s: string) => {
    const bin = atob(s.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat((4 - (s.length % 4)) % 4));
    return Uint8Array.from(bin, (c) => c.charCodeAt(0));
  },
};
export { b64u };

export const newLinkKeys = () => crypto.subtle.generateKey({ name: "ECDH", namedCurve: "P-256" }, false, ["deriveBits"]) as Promise<CryptoKeyPair>;

export async function exportPublic(k: CryptoKey): Promise<string> {
  return b64u.from(await crypto.subtle.exportKey("raw", k));
}

/** The 8-character code for linking without a QR code: a hash of the new
 * device's public key (the server computes the same, internal/api/link.go),
 * so the phone can check it got this device's key. */
export async function linkCode(pub: string): Promise<string> {
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  const h = new Uint8Array(await crypto.subtle.digest("SHA-256", new TextEncoder().encode("xmm-link-code:" + pub)));
  let n = 0n;
  for (const b of h.subarray(0, 5)) n = (n << 8n) | BigInt(b);
  let out = "";
  for (let i = 0; i < 8; i++) {
    out = alphabet[Number(n & 31n)] + out;
    n >>= 5n;
  }
  return out;
}
export const normCode = (c: string) => c.replace(/[\s-]/g, "").toUpperCase();

/** Shared AES key between the two devices (ECDH + HKDF). */
export async function linkKey(priv: CryptoKey, peerPublic: string): Promise<CryptoKey> {
  const pub = await crypto.subtle.importKey("raw", b64u.to(peerPublic), { name: "ECDH", namedCurve: "P-256" }, false, []);
  const bits = await crypto.subtle.deriveBits({ name: "ECDH", public: pub }, priv, 256);
  const hk = await crypto.subtle.importKey("raw", bits, "HKDF", false, ["deriveKey"]);
  return crypto.subtle.deriveKey(
    { name: "HKDF", hash: "SHA-256", salt: new TextEncoder().encode("xmm-link-v1"), info: new TextEncoder().encode("history transfer") },
    hk,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}

/** The account's automatic backup key (from the server). */
export function backupKey(k: string): Promise<CryptoKey> {
  return crypto.subtle.importKey("raw", b64u.to(k), "AES-GCM", false, ["encrypt", "decrypt"]);
}

export const BACKUP_ITERATIONS = 600_000;

// ---- self-contained backup files (Google Drive, iCloud, a file you keep) ------------
// "XMMB1\n" + one JSON line (key derivation parameters, summary) + the sealed
// bundle. Encrypted with the backup password before it leaves the device, so
// the cloud provider only stores ciphertext.

export interface BackupHeader {
  account: string;
  salt: string;
  iterations: number;
  messages: number;
  created_at: number;
}

const MAGIC = "XMMB1\n";

export async function packBackup(password: string, b: Bundle): Promise<{ blob: Blob; header: BackupHeader }> {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const key = await passwordKey(password, salt, BACKUP_ITERATIONS);
  const header: BackupHeader = { account: b.account, salt: b64u.from(salt), iterations: BACKUP_ITERATIONS, messages: b.messages.length, created_at: Math.floor(Date.now() / 1000) };
  return { blob: new Blob([MAGIC, JSON.stringify(header) + "\n", await seal(key, b)], { type: "application/octet-stream" }), header };
}

export function backupHeader(data: ArrayBuffer): { header: BackupHeader; body: ArrayBuffer } {
  const bytes = new Uint8Array(data);
  const nl = bytes.indexOf(10, MAGIC.length);
  if (new TextDecoder().decode(bytes.subarray(0, MAGIC.length)) !== MAGIC || nl < 0) throw new Error("this isn't an xmm backup file");
  const header = JSON.parse(new TextDecoder().decode(bytes.subarray(MAGIC.length, nl))) as BackupHeader;
  return { header, body: data.slice(nl + 1) };
}

export async function unpackBackup(password: string, data: ArrayBuffer): Promise<Bundle> {
  const { header, body } = backupHeader(data);
  if (header.iterations < 100_000 || header.iterations > 10_000_000) throw new Error("damaged backup file");
  return unseal(await passwordKey(password, b64u.to(header.salt) as Uint8Array<ArrayBuffer>, header.iterations), body);
}

export async function passwordKey(password: string, salt: Uint8Array<ArrayBuffer>, iterations: number): Promise<CryptoKey> {
  const base = await crypto.subtle.importKey("raw", new TextEncoder().encode(password), "PBKDF2", false, ["deriveKey"]);
  return crypto.subtle.deriveKey({ name: "PBKDF2", hash: "SHA-256", salt, iterations }, base, { name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]);
}

/** QR payload: xmm-link:v1:<request id>:<new device ECDH public key> */
export const linkURI = (id: string, pub: string) => `xmm-link:v1:${id}:${pub}`;

export function parseLinkURI(s: string): { id: string; pub: string } | null {
  const m = /^xmm-link:v1:([0-9a-f]{16}):([A-Za-z0-9_-]{80,100})$/.exec(s.trim());
  return m ? { id: m[1], pub: m[2] } : null;
}
